import { Injectable, Inject } from '@angular/core';

import { Observable } from 'rxjs';
import {
  take,
  map,
  distinctUntilChanged,
  startWith,
  withLatestFrom
} from 'rxjs/operators';
import { combineLatest, zip } from 'rxjs';
import { Store } from '@ngrx/store';

import {
  ExportService,
  ConfigProviderToken,
  ExportConfigProvider,
  ExportConfig
} from '../types/export.types';
import {
  ExportableDealDetailsTabs,
  ExportModalConfigOption,
  ExportModalOption,
  ExportServiceOption
} from '../types/export-modal.types';
import { ExcelWorkSheet } from '../utils/export/excel/worksheet';
import { selectActiveTab } from '../state/reducer';
import { isDesignations } from '../helpers/designations.helper';

@Injectable()
export class ExportFacade {
  private exportServices: ExportService[] = this.configProvider.getExportServices();
  public exportOptions$: Observable<ExportServiceOption[]> = combineLatest(
    this.exportServices.map((s) => s.dialogOption$)
  ).pipe(
    withLatestFrom(this.store.select(selectActiveTab)),
    map(([options, activeTab]) =>
      isDesignations(activeTab)
        ? options.filter((o) => isDesignations(o?.name))
        : options
    ),
    map((filteredOptions) => filteredOptions.filter((o) => !!o))
  );

  public exportDisabled$: Observable<boolean> = combineLatest(
    this.exportServices.map((s) => s.isReady$)
  ).pipe(
    withLatestFrom(this.store.select(selectActiveTab)),
    map(
      ([servicesReadyStatuses, activeTab]) =>
        !(
          servicesReadyStatuses.every((isReady) => isReady) ||
          (activeTab === ExportableDealDetailsTabs.designations &&
            servicesReadyStatuses[4])
        )
    ),
    startWith(true),
    distinctUntilChanged()
  );

  public getFilename(): Observable<string> {
    return this.configProvider.getFilename();
  }

  public exportWorksheets(
    selectedOptions: ExportModalOption[]
  ): Observable<ExcelWorkSheet[]> {
    const selectedTabs = selectedOptions.map((option) => {
      const service = this.exportServices.find(
        (exportService) => exportService.dialogOptionName === option.name
      );

      const exportConfig = option.configOptions.reduce(
        (
          config: ExportConfig<string>,
          configOption: ExportModalConfigOption<string>
        ) => {
          config[configOption.name] = configOption.isChecked;
          return config;
        },
        {}
      );

      return {
        service,
        exportConfig
      };
    });

    return zip(
      ...selectedTabs.map(({ service, exportConfig }) =>
        service.getWorksheets$(exportConfig)
      )
    ).pipe(
      take(1),
      map((worksheetPacks: ExcelWorkSheet[][]) => {
        return worksheetPacks.reduce(
          (acc, worksheets) => [...acc, ...worksheets],
          []
        );
      })
    );
  }

  constructor(
    @Inject(ConfigProviderToken)
    private configProvider: ExportConfigProvider,
    private store: Store
  ) {}
}
